import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Col, Empty, Input, Row, Spin, Typography } from "antd";
import { useMemo, useState } from "react";
import { useAppSelector } from "../../../redux/hooks";
import { Policy, Risk } from "../../../redux/reducers/types/commonTypes";
import CreatePolicyModal from "../../components/dashboard/modals/CreatePolicyModal";
import SlimPolicyCard from "../../components/policies/publicCard/SlimPolicyCard";
import { useAvailablePolicies, useUserPolicies } from "../../hooks/usePolicies";
import { useUserRisks } from "../../hooks/useRisks";
import { isPolicyMember } from "../../utils/policyUtils";

const { Title, Text } = Typography;

// Risk type keywords mapping for search
const RISK_TYPE_KEYWORDS: Record<string, string[]> = {
    cell_phone: ["phone", "cellphone", "cell phone", "smartphone", "iphone", "android", "mobile"],
    audio_equipment: ["audio", "speaker", "headphone", "earphone", "sound", "music", "stereo"],
    generic_property: ["diamond", "ring", "jewelry", "watch", "laptop", "computer", "tablet", "camera", "tv", "television", "furniture", "bike", "bicycle", "car", "vehicle", "pet", "dog", "cat"],
};

export default function Home() {
    const [search, setSearch] = useState("");
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [initialSearchQuery, setInitialSearchQuery] = useState("");
    
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    
    // Fetch data using React Query
    const { data: userPoliciesData, isLoading: isLoadingUserPolicies } = useUserPolicies();
    const { data: availablePoliciesData, isLoading: isLoadingAvailablePolicies } = useAvailablePolicies(1, 100);
    const { data: userRisksData, isLoading: isLoadingUserRisks } = useUserRisks();
    
    const userPolicies = useMemo(() => userPoliciesData?.results || [], [userPoliciesData]);
    const publicPolicies = useMemo(() => availablePoliciesData?.results || [], [availablePoliciesData]);
    
    // Transform user risks from array to object grouped by policy ID
    const userRisksByPolicy = useMemo(() => {
        if (!userRisksData) return {};
        return userRisksData.reduce((acc: { [key: number]: Risk[] }, risk: Risk) => {
            if (!acc[risk.policy]) {
                acc[risk.policy] = [];
            }
            acc[risk.policy].push(risk);
            return acc;
        }, {});
    }, [userRisksData]);
    
    // Get policies where user has risks but isn't a member
    const policiesWithRisksButNotMember = useMemo(() => {
        if (!userRisksByPolicy || Object.keys(userRisksByPolicy).length === 0 || !publicPolicies) return [];
        
        const policyIdsWithRisks = new Set(
            Object.keys(userRisksByPolicy).map(Number)
        );
        
        return publicPolicies.filter((policy: Policy) => {
            const hasRisk = policyIdsWithRisks.has(policy.id);
            const isMember = isPolicyMember(currentUser, policy);
            return hasRisk && !isMember;
        });
    }, [userRisksByPolicy, publicPolicies, currentUser]);
    
    // Filter policies based on search
    const searchResults = useMemo(() => {
        if (!search.trim()) return [];
        
        const searchLower = search.toLowerCase().trim();
        
        // Find matching risk types from keywords
        const matchingRiskTypes: string[] = [];
        Object.entries(RISK_TYPE_KEYWORDS).forEach(([riskType, keywords]) => {
            if (keywords.some(keyword => searchLower.includes(keyword))) {
                matchingRiskTypes.push(riskType);
            }
        });
        
        // If no keyword match, try direct match with risk type names
        if (matchingRiskTypes.length === 0) {
            Object.keys(RISK_TYPE_KEYWORDS).forEach(riskType => {
                if (searchLower.includes(riskType.replace("_", " "))) {
                    matchingRiskTypes.push(riskType);
                }
            });
        }
        
        // Search in public policies
        return publicPolicies.filter((policy: Policy) => {
            // Check if policy has matching risk types
            if (matchingRiskTypes.length > 0) {
                return policy.available_underlying_insured_types?.some(
                    (type: string) => matchingRiskTypes.includes(type)
                );
            }
            
            // Fallback: search in policy name and description
            const nameMatch = policy.name?.toLowerCase().includes(searchLower);
            const descMatch = policy.description?.toLowerCase().includes(searchLower);
            
            return nameMatch || descMatch;
        });
    }, [search, publicPolicies]);
    
    const isSearching = search.trim().length > 0;
    const isLoading = isLoadingUserPolicies || isLoadingAvailablePolicies || isLoadingUserRisks;
    
    const handleCreatePolicy = () => {
        if (search.trim() && searchResults.length === 0) {
            setInitialSearchQuery(search.trim());
        } else {
            setInitialSearchQuery("");
        }
        setIsCreateModalVisible(true);
    };
    
    const handleSearchEnter = () => {
        if (search.trim() && searchResults.length === 0) {
            setInitialSearchQuery(search.trim());
            setIsCreateModalVisible(true);
        }
    };
    
    if (isLoading) {
        return (
            <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
                <Spin size="large" />
            </div>
        );
    }
    
    return (
        <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
            <Row justify="center" style={{ marginBottom: "48px", marginTop: "32px" }}>
                <Col xs={24} sm={20} md={16} lg={12} xl={10}>
                    <Input
                        size="large"
                        prefix={<SearchOutlined style={{ color: "#1890ff" }} />}
                        placeholder="What would you like to insure?"
                        value={search}
                        onPressEnter={handleSearchEnter}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            borderRadius: "8px",
                            fontSize: "16px",
                            padding: "12px 16px",
                        }}
                    />
                </Col>
            </Row>
            
            {isSearching && (
                <div>
                    {searchResults.length > 0 ? (
                        <>
                            <Title level={4} style={{ marginBottom: "24px" }}>
                                Matching policies ({searchResults.length})
                            </Title>
                            <div>
                                {searchResults.map((policy: Policy) => (
                                    <SlimPolicyCard key={policy.id} policy={policy} />
                                ))}
                            </div>
                        </>
                    ) : (
                        <Row justify="center" style={{ marginTop: "64px" }}>
                            <Col xs={24} sm={20} md={16} lg={12}>
                                <Empty
                                    description={
                                        <div>
                                            <Text type="secondary" style={{ fontSize: "16px", display: "block", marginBottom: "16px" }}>
                                                No policies found for "{search}"
                                            </Text>
                                            <Button
                                                type="primary"
                                                size="large"
                                                icon={<PlusOutlined />}
                                                onClick={handleCreatePolicy}
                                                style={{ borderRadius: "8px" }}
                                            >
                                                Create new policy
                                            </Button>
                                        </div>
                                    }
                                />
                            </Col>
                        </Row>
                    )}
                </div>
            )}
            
            {!isSearching && (
                <>
                    {userPolicies && userPolicies.length > 0 && (
                        <div style={{ marginBottom: "48px" }}>
                            <Title level={4} style={{ marginBottom: "24px" }}>
                                My policies ({userPolicies.length})
                            </Title>
                            <div>
                                {userPolicies.map((policy: Policy) => (
                                    <SlimPolicyCard key={policy.id} policy={policy} />
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {policiesWithRisksButNotMember.length > 0 && (
                        <div>
                            <Title level={4} style={{ marginBottom: "24px" }}>
                                Policies with my quotes ({policiesWithRisksButNotMember.length})
                            </Title>
                            <Text type="secondary" style={{ display: "block", marginBottom: "16px" }}>
                                You've created quotes for these policies but haven't joined yet
                            </Text>
                            <div>
                                {policiesWithRisksButNotMember.map((policy: Policy) => (
                                    <SlimPolicyCard key={policy.id} policy={policy} />
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {(!userPolicies || userPolicies.length === 0) && 
                     policiesWithRisksButNotMember.length === 0 && (
                        <Row justify="center" style={{ marginTop: "64px" }}>
                            <Col xs={24} sm={20} md={16} lg={12}>
                                <Empty
                                    description={
                                        <div>
                                            <Text type="secondary" style={{ fontSize: "16px", display: "block", marginBottom: "16px" }}>
                                                You don't have any policies yet
                                            </Text>
                                            <Button
                                                type="primary"
                                                size="large"
                                                icon={<PlusOutlined />}
                                                onClick={handleCreatePolicy}
                                                style={{ borderRadius: "8px" }}
                                            >
                                                Create your first policy
                                            </Button>
                                        </div>
                                    }
                                />
                            </Col>
                        </Row>
                    )}
                </>
            )}
            
            <CreatePolicyModal
                isVisible={isCreateModalVisible}
                setIsVisible={setIsCreateModalVisible}
                initialSearchQuery={initialSearchQuery}
            />
        </div>
    );
}

